/**
 * How the product says things about the work: counts, lists, changes and
 * guesses. Every sentence the review screens build goes through these, so a
 * margin change always reads "−13.3 pts" and a guess always reads as one.
 */

const MINUS = "−";

export const count = (n: number, noun: string, plural = `${noun}s`) =>
  `${n.toLocaleString("en-US")} ${n === 1 ? noun : plural}`;

/**
 * Names in a sentence, the first `max` of them and a count of the rest:
 * every name when one more than `max` would only say "and 1 other".
 */
export const listOf = (names: readonly string[], max = 2) => {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length <= max + 1)
    return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
  const rest = names.length - max;
  return `${names.slice(0, max).join(", ")} and ${count(rest, "other")}`;
};

/**
 * A change in a rate, in points, signed with a true minus: 0.007 is
 * "+0.7 pts", −0.133 is "−13.3 pts", −11.829 is "−1,182.9 pts".
 */
export const points = (change: number, digits = 1) => {
  const value = Math.abs(change * 100);
  if (Number(value.toFixed(digits)) === 0) return "0 pts";
  const shown = value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${change < 0 ? MINUS : "+"}${shown} pts`;
};

/**
 * A signed amount of money in whole units, with a true minus: "+$761",
 * "−$980", "+€1,200". `currency` is an ISO 4217 code (US dollars when left
 * out, or when the code is one Intl cannot format).
 */
export const moneyChange = (change: number, currency = "USD") => {
  const format = (code: string) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(Math.abs(change));
  let value: string;
  try {
    value = format(currency);
  } catch {
    value = format("USD");
  }
  return `${change < 0 ? MINUS : "+"}${value}`;
};

/**
 * A model's confidence as the word a person would use. Never a percentage:
 * "100%" from a guess reads as a measurement.
 */
export const likelihood = (confidence: number | null | undefined) => {
  if (confidence === null || confidence === undefined) return "Possibly";
  if (confidence >= 0.9) return "Almost certainly";
  if (confidence >= 0.65) return "Probably";
  if (confidence >= 0.35) return "Possibly";
  return "Unlikely";
};

/**
 * A raw flag's text split for display: the words before its colon and the
 * detail after ("Duplicate equipment: invoiced 2×, sold 1×"); one written
 * without a colon is all rule. Display only: it is not the rule a finding is
 * grouped by. The API works that out (it also strips figures from a flag
 * without a colon) and sends it as the finding's `rule`, which a finding's
 * screen reads instead of splitting its flag again.
 */
export const splitFlag = (flag: string) => {
  const at = flag.indexOf(":");
  if (at <= 0) return { rule: flag.trim(), detail: "" };
  return {
    rule: flag.slice(0, at).trim(),
    detail: flag.slice(at + 1).trim(),
  };
};

/**
 * What to call a reporting period of `days` days: a report run weekly says
 * "week", one run monthly says "month", and fourteen days is a "pay period"
 * (biweekly reports close with payroll). Anything irregular is a "period".
 * The API names periods the same way (`Profitability::PeriodLabel.noun`).
 */
export const periodNoun = (days: number) => {
  if (days <= 1) return "day";
  if (days >= 5 && days <= 8) return "week";
  if (days === 14) return "pay period";
  if (days >= 27 && days <= 31) return "month";
  if (days >= 89 && days <= 92) return "quarter";
  return "period";
};

/**
 * Whether a change is good news: up is good for revenue and margin, down is
 * good for a cost. Colour follows this, so a falling cost reads green.
 */
export const judge = (
  change: number | null | undefined,
  better: "higher" | "lower" | null | undefined = "higher",
) => {
  if (!change) return "neutral" as const;
  const up = change > 0;
  return (better === "lower" ? !up : up) ? ("good" as const) : ("bad" as const);
};
